import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CircleDot, Rows3, Rabbit, Repeat, Ghost, Palette, Sparkles, ChevronRight, Play } from "lucide-react";
import GamePreview from "./GamePreview";

const FEATURED = [
  { slug: "bullseye", icon: CircleDot, title: "Bullseye", hook: "Tap the instant the ring collapses onto the mark. Pure timing.", to: "/games/bullseye", isNew: true },
  { slug: "cascade", icon: Rows3, title: "Cascade", hook: "Four lanes of falling tiles, endless speed, one rhythm.", to: "/games/cascade", isNew: true },
  { slug: "sprint", icon: Rabbit, title: "Sprint", hook: "Swerve left and right to survive an endless, accelerating run.", to: "/games/sprint", isNew: true },
  { slug: "flip", icon: Repeat, title: "Flip", hook: "The target rule flips mid-game. Beat your own autopilot.", to: "/games/flip" },
  { slug: "ghost-tap", icon: Ghost, title: "Ghost Tap", hook: "A dot vanishes behind a wall — tap the exact moment it crosses.", to: "/games/ghost-tap" },
  { slug: "color-trap", icon: Palette, title: "Color Trap", hook: "The word says one color, printed in another. Tap the ink.", to: "/games/color-trap" },
];

const ROTATE_MS = 4000;

export default function FeaturedGames({ randomStart = false, showHeader = true }) {
  const [i, setI] = useState(() => (randomStart ? Math.floor(Math.random() * FEATURED.length) : 0));
  const [paused, setPaused] = useState(false);
  const f = FEATURED[i];
  const go = useCallback((n) => setI((n + FEATURED.length) % FEATURED.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((v) => (v + 1) % FEATURED.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="featured-games">
      {showHeader ? (
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#39FF14]">
              <Sparkles size={13} /> Featured game
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2">More than a test — it's a playground</h2>
          </div>
          <Link to="/games" className="hidden sm:flex items-center gap-1 text-[#39FF14] text-sm font-semibold hover:gap-2 transition-all">All games <ChevronRight size={16} /></Link>
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="text-[#39FF14]" size={18} />
          <h2 className="font-display font-black text-2xl">Featured game</h2>
          <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 text-white/50 border border-white/10">Fresh pick every visit</span>
        </div>
      )}

      <div className="group card-surface rounded-3xl overflow-hidden grid md:grid-cols-2"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        {/* animated preview */}
        <div className="relative h-56 md:h-72 overflow-hidden order-1 md:order-none"
          style={{ background: "radial-gradient(ellipse 70% 80% at 50% 120%, rgba(57,255,20,0.16), #070707 72%)" }}>
          <AnimatePresence mode="wait">
            <motion.div key={f.slug} className="absolute inset-0"
              initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}>
              <div className="absolute inset-0" style={{ transform: "scale(2.6)", transformOrigin: "center" }}>
                <GamePreview type={f.slug} showIcon={false} className="absolute inset-0 overflow-hidden" />
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 80px rgba(0,0,0,0.6)" }} />
        </div>

        {/* text */}
        <div className="p-8 flex flex-col justify-center">
          <AnimatePresence mode="wait">
            <motion.div key={f.slug}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-[#39FF14]/12 flex items-center justify-center"><f.icon className="text-[#39FF14]" size={22} /></div>
                <h3 className="font-display font-black text-3xl" data-testid="featured-title">{f.title}</h3>
                {f.isNew && <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/30">New</span>}
              </div>
              <p className="text-white/55 text-base max-w-md mb-6">{f.hook}</p>
              <Link to={f.to} data-testid="featured-play-button"
                className="btn-glow bg-[#39FF14] text-black font-display font-extrabold px-7 py-3.5 rounded-full inline-flex items-center gap-2 w-fit">
                <Play size={17} className="fill-black" /> Play {f.title}
              </Link>
            </motion.div>
          </AnimatePresence>

          {/* selector chips */}
          <div className="flex flex-wrap gap-2 mt-8">
            {FEATURED.map((g, n) => (
              <button key={g.slug} onClick={() => go(n)} data-testid={`featured-chip-${g.slug}`}
                aria-label={`Show ${g.title}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${n === i ? "bg-[#39FF14] text-black border-[#39FF14]" : "bg-white/5 text-white/55 border-white/10 hover:border-[#39FF14]/40 hover:text-white"}`}>
                {g.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
